/**
 * 全局时区处理 —— 统一在这里把服务器的 UTC 转成 CST 显示。
 *
 * 背景：
 *   - Postgres `timestamptz` 通过 PostgREST 返回 ISO 字符串（`...Z` / `+00:00`），
 *     语义是 UTC instant
 *   - dayjs 默认按浏览器本地时区格式化。开发机在 CST 时刚好"看着对"，
 *     不在 CST 的访问者就会错位
 *   - 后端 RPC 里"今天"是 `(now() at time zone 'Asia/Shanghai')::date`，
 *     前端必须按上海时区对齐才不会脏
 *
 * 约定：
 *   - **timestamptz 字段** → `formatCST(iso, fmt?)`
 *   - **Postgres `date` 字段**（仅日期，无 tz 概念）→ `formatDateOnly(d, fmt?)`
 *   - "今天 / 今周几" 类判断 → `nowCST()`
 *   - DatePicker 选出的 dayjs 值 → `pickerToUTCISO(d)` 再送给后端
 *
 * 这个 module 必须在任何 `dayjs.tz(...)` 调用前 import 一次（main.tsx 已处理）。
 */

import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export const APP_TZ = 'Asia/Shanghai'

dayjs.tz.setDefault(APP_TZ)

/** timestamptz 字段（UTC instant）→ CST 字符串。null/undefined 给 dash */
export function formatCST(
  input: string | number | Date | null | undefined,
  fmt: string = 'YYYY-MM-DD HH:mm:ss',
): string {
  if (input == null || input === '') return '—'
  return dayjs(input).tz(APP_TZ).format(fmt)
}

/** Postgres `date` 字段（无 tz）→ 直接按字面值格式化，不做 tz 转换 */
export function formatDateOnly(
  input: string | null | undefined,
  fmt: string = 'YYYY-MM-DD',
): string {
  if (input == null || input === '') return '—'
  return dayjs(input).format(fmt)
}

/** "现在" 的 dayjs 对象，已切到 CST。用于今日 / 今周几等判断 */
export function nowCST(): Dayjs {
  return dayjs().tz(APP_TZ)
}

/**
 * 把服务器返回的 timestamptz 转成 CST-anchored dayjs，用于 DatePicker.value 等
 * 需要把 wall-clock 按 CST 展示的场景。
 */
export function toCSTDayjs(input: string | null | undefined): Dayjs | null {
  if (input == null || input === '') return null
  return dayjs(input).tz(APP_TZ)
}

/**
 * 把 DatePicker 选出来的 dayjs 值（浏览器本地 tz 解读的 wall-clock）
 * 按 CST 重新解读，输出 UTC ISO 给后端。
 *
 * 例：用户选 "2026-06-02 00:00:00"
 *   - 旧做法 `.toISOString()`：按浏览器 tz 算成 UTC，CST 浏览器得到 "2026-06-01T16:00:00Z"（正确）；
 *     非 CST 浏览器（如 UTC）得到 "2026-06-02T00:00:00Z"（错位 8 小时）
 *   - 新做法：永远当 CST 解读 → 永远输出 "2026-06-01T16:00:00Z"
 */
export function pickerToUTCISO(d: Dayjs | null | undefined): string | null {
  if (!d) return null
  return dayjs.tz(d.format('YYYY-MM-DDTHH:mm:ss'), APP_TZ).toISOString()
}
